'use client'

/**
 * The client half of the external HTTP way in: hear that a capture arrived, fetch it, write it.
 *
 * Out of `src/providers/EntryUpdateProvider.tsx` on #402. Two things changed with the move.
 *
 * **The six-case `switch` is gone.** It was a second, hand-written copy of the from-enrichment
 * mapping the in-app form already had, and the two disagreed: this one wrote `metadata.coverUrl`,
 * `bookSummary`, `musicSummary`, `artistImageUrl` and `labelImageUrl`, none of which any surface
 * reads — every media row reads `entry.imageUrl`. Both paths call `buildMediaCreateData` and
 * `mediaEntryDraft` now, which is what ADR 0009 meant by two adapters rather than two
 * implementations.
 *
 * **The person links are written.** `peopleIds` was computed here and reached a `console.log` and
 * nothing else (#164): a capture naming someone produced no link, so the entry never appeared on
 * that person's timeline. It goes on the `EntryDraft`, which `document/operations/entries.ts`
 * turns into `doc.links` entries — for every kind, because the draft is the seam rather than any
 * one slice's create.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react'
import { createClient } from '@/account/client'
import {
  useDocumentOps,
  useDocumentView,
  useDocumentStatus,
  type EntryDraft,
} from '@/document'
import type { RealtimeChannel } from '@supabase/supabase-js'
import { isMediaKind, mediaEntryDraft } from '@/entries'
import type { PendingCapture } from '../types'
import type { EnrichedMediaResult } from '@/enrichment'
import { buildMediaCreateData, type MediaAnalysis } from './from-analysis'
import { ENTRY_CREATED_EVENT, entryUpdatesChannel } from './broadcast'
import { matchPeople, matchProject, matchTopics } from './match'

/** PendingCapture as returned by GET /api/pending-entries (with id field). */
interface PendingCaptureWithId extends PendingCapture {
  id: string
}

interface EntryUpdate {
  entryId: string
  entryType: string
  timestamp: number
}

interface EntryUpdateContextValue {
  lastUpdate: EntryUpdate | null
  subscribe: (callback: (update: EntryUpdate) => void) => () => void
}

const EntryUpdateContext = createContext<EntryUpdateContextValue | null>(null)

export function EntryUpdateProvider({
  children,
  userId,
}: {
  children: ReactNode
  userId: string
}) {
  const [lastUpdate, setLastUpdate] = useState<EntryUpdate | null>(null)
  const subscribersRef = useRef<Set<(update: EntryUpdate) => void>>(new Set())
  const doc = useDocumentView()
  const ready = useDocumentStatus()
  const ops = useDocumentOps()
  const processingRef = useRef(false)

  // Stable refs to avoid channel recreation on every doc change
  const docRef = useRef(doc)
  const opsRef = useRef(ops)
  const readyRef = useRef(ready)
  docRef.current = doc
  opsRef.current = ops
  readyRef.current = ready

  const subscribe = useCallback((callback: (update: EntryUpdate) => void) => {
    subscribersRef.current.add(callback)
    return () => {
      subscribersRef.current.delete(callback)
    }
  }, [])

  const createEntryFromCapture = useCallback(
    (capture: PendingCaptureWithId) => {
      const currentDoc = docRef.current
      if (!currentDoc) {
        console.error('[capture-client] Cannot create entry: doc unavailable')
        return null
      }

      // Use pre-analyzed data from server (populated in capture API)
      const entryType = capture.analysis?.entryType || capture.entryType
      const title = capture.analysis?.title || capture.title
      const enrichment = capture.enrichment

      // Build metadata from capture metadata + enrichment
      const metadata: Record<string, unknown> = {
        ...(capture.metadata || {}),
        pendingCaptureId: capture.id,
      }

      // The media kinds go through the one from-enrichment mapping the in-app form uses. Only
      // `task`'s defaults are this path's own: the external path has no user to ask, so an
      // arriving task lands in the inbox.
      let mediaDraft: EntryDraft | null = null
      if (entryType === 'task') {
        metadata.category = metadata.category || 'personal'
        metadata.important = metadata.important || false
        metadata.status = 'inbox'
      } else if (isMediaKind(entryType) && enrichment) {
        const enrichedResult = {
          type: entryType,
          name: title,
          enriched: enrichment,
          alternatives: [],
          confidence: 'high',
        } as unknown as EnrichedMediaResult
        const created = buildMediaCreateData(
          entryType,
          { title, name: title } as unknown as MediaAnalysis,
          enrichedResult,
        )
        mediaDraft = mediaEntryDraft(entryType, created.data)
        Object.assign(metadata, mediaDraft.metadata ?? {})
      }

      // Client-side matching against the local Automerge document. The two fuzzy matchers are
      // not a divergence to collapse onto the form: they exist because this path has no user to
      // ask.
      const searchText = `${title} ${capture.content}`.toLowerCase()
      const topicIds = matchTopics(currentDoc, searchText, entryType)
      const projectId = matchProject(currentDoc, searchText)
      const peopleIds = matchPeople(currentDoc, searchText)

      // Use enrichment title if available (properly formatted from external API)
      const entryTitle =
        enrichment?.title && typeof enrichment.title === 'string'
          ? enrichment.title
          : title

      // Use enrichment imageUrl as entry-level imageUrl for media types
      const imageUrl =
        enrichment?.imageUrl && typeof enrichment.imageUrl === 'string'
          ? enrichment.imageUrl
          : capture.imageUrl

      const id = opsRef.current.createEntry({
        type: entryType,
        title: entryTitle,
        content: capture.content,
        url: capture.url || undefined,
        imageUrl: imageUrl || undefined,
        metadata,
        projectId,
        topicIds,
        // #164: computed here since the path was written, and written nowhere until #402
        peopleIds,
        ...(mediaDraft?.genres ? { genres: mediaDraft.genres } : {}),
      })

      console.log('[capture-client] Entry created in Automerge', {
        id,
        type: entryType,
        title: entryTitle,
        url: capture.url ?? '(not provided)',
        hasUrl: !!capture.url,
        hasEnrichment: !!enrichment,
        topicCount: topicIds.length,
        peopleCount: peopleIds.length,
      })

      return id
    },
    [], // No deps — reads from refs
  )

  const processPendingCaptures = useCallback(async () => {
    if (!readyRef.current || !docRef.current) {
      console.log('[capture-client] Skipping: doc not ready', {
        ready: readyRef.current,
        hasDoc: !!docRef.current,
      })
      return
    }
    if (processingRef.current) {
      console.log('[capture-client] Skipping: already processing')
      return
    }
    processingRef.current = true
    console.log('[capture-client] Processing pending captures...')

    try {
      const response = await fetch('/api/pending-entries')
      if (!response.ok) {
        console.error('[capture-client] Fetch pending failed:', response.status)
        return
      }

      const { captures } = await response.json()

      if (!captures || captures.length === 0) {
        console.log('[capture-client] No pending captures found')
        return
      }

      console.log('[capture-client] Found captures:', captures.length)

      // Load-bearing invariant: skip any capture whose id already appears as
      // `metadata.pendingCaptureId`. This is the only thing preventing duplicate entries when the
      // ACK delete fails, since that path is deliberately non-blocking. Do not weaken it.
      const currentDoc = docRef.current!
      const processedCaptureIds = new Set<string>()
      if (currentDoc.entries) {
        for (const entry of Object.values(currentDoc.entries)) {
          const pcId = entry.metadata?.pendingCaptureId
          if (typeof pcId === 'string') {
            processedCaptureIds.add(pcId)
          }
        }
      }

      console.log('[capture-client] Dedup set built', {
        processedCaptureIds: processedCaptureIds.size,
      })

      const processedIds: string[] = []

      for (const capture of captures as PendingCaptureWithId[]) {
        // Dedup: skip captures already turned into Automerge entries
        if (processedCaptureIds.has(capture.id)) {
          console.log(
            '[capture-client] Skipping already-processed capture:',
            capture.id,
          )
          processedIds.push(capture.id)
          continue
        }

        console.log('[capture-client] Processing capture:', {
          type: capture.analysis?.entryType || capture.entryType,
          title: capture.analysis?.title || capture.title,
          contentLength: capture.content?.length ?? 0,
          hasAnalysis: !!capture.analysis,
          hasEnrichment: !!capture.enrichment,
        })

        // Create entry directly from pre-analyzed capture data
        // (analysis + enrichment already populated server-side;
        //  topic/people matching happens client-side in createEntryFromCapture)
        const entryId = createEntryFromCapture(capture)

        if (entryId) {
          console.log('[capture-client] Entry created successfully:', entryId)
          processedIds.push(capture.id)

          const entryType = capture.analysis?.entryType || capture.entryType
          const update: EntryUpdate = {
            entryId,
            entryType,
            timestamp: capture.createdAt,
          }

          setLastUpdate(update)
          subscribersRef.current.forEach((callback) => callback(update))
        } else {
          console.error(
            '[capture-client] Failed to create entry from capture:',
            capture.title,
          )
        }
      }

      // ACK: delete processed captures from server
      if (processedIds.length > 0) {
        try {
          const ackResponse = await fetch('/api/pending-entries', {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ids: processedIds }),
          })
          if (ackResponse.ok) {
            const ackData = await ackResponse.json()
            console.log(
              '[capture-client] ACK successful, deleted:',
              ackData.deleted,
            )
          } else {
            console.error('[capture-client] ACK failed:', ackResponse.status)
          }
        } catch (ackError) {
          // Log but don't block — captures will be re-fetched and deduped next time
          console.error('[capture-client] ACK request failed:', ackError)
        }
      }
    } catch (error) {
      console.error('[capture-client] Processing failed:', error)
    } finally {
      processingRef.current = false
    }
  }, [createEntryFromCapture]) // Stable — createEntryFromCapture has no deps

  // Stable ref for the callback so channel effect doesn't depend on it
  const processPendingCapturesRef = useRef(processPendingCaptures)
  processPendingCapturesRef.current = processPendingCaptures

  // Channel setup — only recreated when userId changes (stable)
  useEffect(() => {
    const supabase = createClient()
    let channel: RealtimeChannel | null = null

    async function setupChannel() {
      channel = supabase.channel(entryUpdatesChannel(userId))

      channel.on('broadcast', { event: ENTRY_CREATED_EVENT }, (payload) => {
        console.log('[capture-client] Broadcast received:', payload)
        processPendingCapturesRef.current()
      })

      const status = await channel.subscribe()
      console.log('[capture-client] Channel subscribed:', status)

      // Check for pending captures on initial connection
      processPendingCapturesRef.current()
    }

    setupChannel()

    return () => {
      console.log('[capture-client] Channel cleanup')
      if (channel) {
        supabase.removeChannel(channel)
      }
    }
  }, [userId])

  // Re-check when Automerge doc becomes ready
  useEffect(() => {
    if (ready && doc) {
      console.log(
        '[capture-client] Doc became ready, checking for pending captures',
      )
      processPendingCapturesRef.current()
    }
  }, [ready, !!doc]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <EntryUpdateContext.Provider value={{ lastUpdate, subscribe }}>
      {children}
    </EntryUpdateContext.Provider>
  )
}

export function useEntryUpdates() {
  const context = useContext(EntryUpdateContext)
  if (!context) {
    throw new Error('useEntryUpdates must be used within EntryUpdateProvider')
  }
  return context
}
